import { gameFormatForAge, formatInfoForAge } from '@4cm/core/gameFormat';
import { FORMATIONS_BY_FORMAT } from '@4cm/core/constants';
import { AGE_GROUP_OPTIONS, COACH_LEVELS, type CoachLevel } from '@4cm/core/profiles';
import type { OnboardingProfile } from '../lib/onboarding';
import './TeamSetupForm.css';

/**
 * The structured, always-editable team setup fields, with FA-driven
 * dropdowns: age group drives the game format (5v5 / 7v7 / 9v9 / 11v11 per
 * the FA youth formats) which in turn drives the formation options. Shared
 * by the onboarding confirm step (bound to the OnboardingProfile state the
 * Brain also writes — so chat + form stay in sync) and Team Settings.
 *
 * Controlled: the caller owns the values + applies `onChange` patches.
 */
export type TeamSetupValues = Pick<
  OnboardingProfile,
  | 'teamName'
  | 'ageGroup'
  | 'formation'
  | 'playerCount'
  | 'coachLevel'
  | 'trainingDays'
  | 'trainingStartTime'
  | 'sessionDurationMins'
  | 'seasonPhase'
  | 'weeksToPlan'
  | 'trainingDaysPerWeek'
>;

interface TeamSetupFormProps {
  values: TeamSetupValues;
  onChange: (patch: Partial<TeamSetupValues>) => void;
  /** Hide the planning-only fields (weeks to plan, season phase) when used
   *  as a pure Team Settings surface. Defaults to false (show everything). */
  compact?: boolean;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

const COACH_LEVEL_LABELS: Record<CoachLevel, string> = {
  NOVICE: 'New to coaching',
  GRASSROOTS: 'Grassroots',
  COMPETITIVE: 'Competitive / travel',
  ADVANCED: 'Advanced / academy',
  ELITE_CAT1: 'Elite (Cat 1)',
};

const SEASON_PHASE_OPTIONS: { value: NonNullable<TeamSetupValues['seasonPhase']>; label: string }[] = [
  { value: 'pre_season', label: 'Pre-season' },
  { value: 'early', label: 'Early season' },
  { value: 'mid', label: 'Mid-season' },
  { value: 'late', label: 'Late season' },
  { value: 'playoffs', label: 'Playoffs' },
  { value: 'offseason', label: 'Off-season' },
];

const DURATION_OPTIONS = [45, 60, 75, 90, 105, 120];

export function TeamSetupForm({ values, onChange, compact = false }: TeamSetupFormProps) {
  const format = gameFormatForAge(values.ageGroup);
  const formatInfo = formatInfoForAge(values.ageGroup);
  const formationOptions = FORMATIONS_BY_FORMAT[format] ?? [];
  // Keep the current formation selectable even if it isn't in the canonical
  // list for this format (e.g. a custom one the coach typed to Brain).
  const formationList =
    values.formation && !formationOptions.includes(values.formation)
      ? [values.formation, ...formationOptions]
      : formationOptions;

  const onAgeChange = (ageGroup: string) => {
    const newFormat = gameFormatForAge(ageGroup);
    const opts = FORMATIONS_BY_FORMAT[newFormat] ?? [];
    const keepFormation = values.formation && opts.includes(values.formation);
    onChange({
      ageGroup,
      formation: keepFormation ? values.formation : formatInfoForAge(ageGroup).defaultFormation,
    });
  };

  const toggleDay = (day: string) => {
    const current = values.trainingDays ?? [];
    const next = current.includes(day) ? current.filter((d) => d !== day) : [...current, day];
    // Keep WEEKDAYS order so 'Mon, Wed' not 'Wed, Mon'.
    const ordered = WEEKDAYS.filter((d) => next.includes(d));
    onChange({ trainingDays: ordered, trainingDaysPerWeek: ordered.length });
  };

  return (
    <div className="tsf" aria-label="Team setup">
      <label className="tsf__field">
        <span className="tsf__label">Team name</span>
        <input
          className="tsf__input"
          type="text"
          value={values.teamName ?? ''}
          onChange={(e) => onChange({ teamName: e.target.value || null })}
          placeholder="e.g. Aurora U12"
        />
      </label>

      <div className="tsf__row">
        <label className="tsf__field">
          <span className="tsf__label">Age group</span>
          <select
            className="tsf__select"
            value={values.ageGroup ?? ''}
            onChange={(e) => onAgeChange(e.target.value)}
            data-testid="tsf-age"
          >
            <option value="" disabled>Select…</option>
            {AGE_GROUP_OPTIONS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </label>

        <div className="tsf__field">
          <span className="tsf__label">Format (FA)</span>
          <div className="tsf__derived" data-testid="tsf-format" title="Set by the FA age group">
            {formatInfo.format} · {formatInfo.starters}-a-side
          </div>
        </div>
      </div>

      <div className="tsf__row">
        <label className="tsf__field">
          <span className="tsf__label">Formation</span>
          <select
            className="tsf__select"
            value={values.formation ?? formatInfo.defaultFormation}
            onChange={(e) => onChange({ formation: e.target.value })}
            data-testid="tsf-formation"
          >
            {formationList.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </label>

        <label className="tsf__field">
          <span className="tsf__label">Squad size</span>
          <input
            className="tsf__input"
            type="number"
            min={4}
            max={30}
            value={values.playerCount ?? ''}
            onChange={(e) => onChange({ playerCount: e.target.value ? Number(e.target.value) : null })}
          />
        </label>
      </div>

      <label className="tsf__field">
        <span className="tsf__label">Coaching level</span>
        <select
          className="tsf__select"
          value={values.coachLevel}
          onChange={(e) => onChange({ coachLevel: e.target.value as CoachLevel })}
          data-testid="tsf-level"
        >
          {COACH_LEVELS.map((l) => (
            <option key={l} value={l}>{COACH_LEVEL_LABELS[l]}</option>
          ))}
        </select>
      </label>

      <div className="tsf__field">
        <span className="tsf__label">Training days</span>
        <div className="tsf__days" role="group" aria-label="Training days">
          {WEEKDAYS.map((d) => {
            const active = (values.trainingDays ?? []).includes(d);
            return (
              <button
                key={d}
                type="button"
                className="tsf__day"
                data-active={active || undefined}
                aria-pressed={active}
                onClick={() => toggleDay(d)}
              >
                {d}
              </button>
            );
          })}
        </div>
      </div>

      <div className="tsf__row">
        <label className="tsf__field">
          <span className="tsf__label">Start time</span>
          <input
            className="tsf__input"
            type="time"
            value={values.trainingStartTime ?? ''}
            onChange={(e) => onChange({ trainingStartTime: e.target.value || null })}
          />
        </label>

        <label className="tsf__field">
          <span className="tsf__label">Session length</span>
          <select
            className="tsf__select"
            value={values.sessionDurationMins}
            onChange={(e) => onChange({ sessionDurationMins: Number(e.target.value) })}
          >
            {DURATION_OPTIONS.map((m) => (
              <option key={m} value={m}>{m} min</option>
            ))}
          </select>
        </label>
      </div>

      {!compact && (
        <div className="tsf__row">
          <label className="tsf__field">
            <span className="tsf__label">Season phase</span>
            <select
              className="tsf__select"
              value={values.seasonPhase ?? ''}
              onChange={(e) => onChange({ seasonPhase: (e.target.value || null) as TeamSetupValues['seasonPhase'] })}
            >
              <option value="" disabled>Select…</option>
              {SEASON_PHASE_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </label>

          <label className="tsf__field">
            <span className="tsf__label">Weeks to plan</span>
            <input
              className="tsf__input"
              type="number"
              min={1}
              max={24}
              value={values.weeksToPlan ?? ''}
              onChange={(e) => onChange({ weeksToPlan: e.target.value ? Number(e.target.value) : null })}
            />
          </label>
        </div>
      )}

      <p className="tsf__note">
        Format follows the FA youth game (5v5 U7–U8, 7v7 U9–U10, 9v9 U11–U12, 11v11 U13+). Age groups use the 1 September cut-off.
      </p>
    </div>
  );
}
